/**
 * Glass Effect Utilities
 * 
 * Provides reusable glassmorphism styles for inline use in JSX components.
 * Use the CSS classes (.glass-effect, .glass-effect-light, etc.) when possible,
 * and use these utilities when you need inline styles or dynamic styling.
 */

export interface GlassEffectStyles {
    background: string;
    boxShadow: string;
    backdropFilter: string;
    WebkitBackdropFilter: string;
    border: string;
}

/**
 * Default glass effect - semi-transparent white with blur
 */
export const glassEffect: GlassEffectStyles = {
    background: 'rgba(255, 255, 255, .7)',
    // background: 'rgba(255, 255, 255, 1)',
    boxShadow: '4.378px 14.595px 27.73px 0 rgba(0, 0, 0, 0.20)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    border: '2px solid rgba(255, 255, 255, 0.8)',
};

/**
 * Light glass effect - more opaque, stronger blur
 */
export const glassEffectLight: GlassEffectStyles = {
    background: 'rgba(255, 255, 255, 0.9)',
    boxShadow: '4.378px 14.595px 27.73px 0 rgba(0, 0, 0, 0.15)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: '1px solid rgba(255, 255, 255, 0.9)',
};

/**
 * Strong glass effect - more transparent, stronger blur
 */
export const glassEffectStrong: GlassEffectStyles = {
    background: 'rgba(255, 255, 255, 0.5)',
    boxShadow: '4.378px 14.595px 27.73px 0 rgba(0, 0, 0, 0.25)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '2px solid rgba(255, 255, 255, 0.6)',
};

/**
 * Dark glass effect - dark background with blur
 */
export const glassEffectDark: GlassEffectStyles = {
    background: 'rgba(0, 0, 0, 0.3)',
    boxShadow: '4.378px 14.595px 27.73px 0 rgba(0, 0, 0, 0.30)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    border: '2px solid rgba(255, 255, 255, 0.2)',
};

/**
 * Custom glass effect generator
 * @param opacity - Background opacity (0-1)
 * @param blur - Blur amount in pixels
 * @param shadowOpacity - Shadow opacity (0-1)
 * @param borderOpacity - Border opacity (0-1)
 */
export const createGlassEffect = (
    opacity: number = 0.7,
    blur: number = 4,
    shadowOpacity: number = 0.2,
    borderOpacity: number = 0.8
): GlassEffectStyles => ({
    background: `rgba(255, 255, 255, ${opacity})`,
    boxShadow: `4.378px 14.595px 27.73px 0 rgba(0, 0, 0, ${shadowOpacity})`,
    backdropFilter: `blur(${blur}px)`,
    WebkitBackdropFilter: `blur(${blur}px)`,
    border: `2px solid rgba(255, 255, 255, ${borderOpacity})`,
});

/**
 * Custom dark glass effect generator
 * @param opacity - Background opacity (0-1)
 * @param blur - Blur amount in pixels
 * @param shadowOpacity - Shadow opacity (0-1)
 * @param borderOpacity - Border opacity (0-1)
 */
export const createDarkGlassEffect = (
    opacity: number = 0.3,
    blur: number = 8,
    shadowOpacity: number = 0.3,
    borderOpacity: number = 0.2
): GlassEffectStyles => ({
    background: `rgba(0, 0, 0, ${opacity})`,
    boxShadow: `4.378px 14.595px 27.73px 0 rgba(0, 0, 0, ${shadowOpacity})`,
    backdropFilter: `blur(${blur}px)`,
    WebkitBackdropFilter: `blur(${blur}px)`,
    border: `2px solid rgba(255, 255, 255, ${borderOpacity})`,
});

